/**
 * Content-addressed store of downloaded map files in Cache Storage: one entry
 * per uncompressed sha256, so a file shared by two bundles is stored once and
 * an unchanged file is never downloaded again (spec §3.3, §3.9). Deleting a
 * bundle deletes only the files no kept bundle references (`sweep`).
 */
export class BundleFileStore {
  static readonly CACHE_NAME = 'metro-map-bundles-v1'

  private readonly caches: CacheStorage

  constructor(caches: CacheStorage) {
    this.caches = caches
  }

  private static key(sha256: string): string {
    return `/__metro-map-files/${sha256}`
  }

  private open(): Promise<Cache> {
    return this.caches.open(BundleFileStore.CACHE_NAME)
  }

  /** The stored file, or `null` when it is absent or not `size` bytes long. */
  async get(sha256: string, size: number): Promise<Blob | null> {
    const res = await (await this.open()).match(BundleFileStore.key(sha256))
    if (res === undefined) return null
    const blob = await res.blob()
    return blob.size === size ? blob : null
  }

  async put(sha256: string, bytes: ArrayBuffer): Promise<void> {
    await (await this.open()).put(BundleFileStore.key(sha256), new Response(bytes))
  }

  /** Deletes every stored file whose sha256 is not in `keep`; returns how many. */
  async sweep(keep: ReadonlySet<string>): Promise<number> {
    const cache = await this.open()
    let removed = 0
    for (const request of await cache.keys()) {
      const sha256 = new URL(request.url).pathname.split('/').pop() ?? ''
      if (!keep.has(sha256) && (await cache.delete(request))) removed += 1
    }
    return removed
  }
}
